import React, { useState, useRef, useEffect } from 'react';
import { db, auth, functions } from '../firebase';
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  limit,
  doc,
  setDoc,
  getDocs
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FaPaperPlane, FaRobot, FaUser, FaSpinner, FaTrash, FaCopy, FaRedo } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import { useOrganization } from '../context/OrganizationContext';
import './ChatGPTInterface.css';

const ChatGPTInterface = ({ chatId, clienteId }) => {
  const { context } = useOrganization();
  const [messages, setMessages] = useState([]);

  // Helper to get chats path (org/personal)
  const getChatsPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'chats']
    : ['clientes', auth.currentUser?.uid || clienteId, 'chats'];
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('Eres un asistente profesional y amigable que ayuda a los clientes con sus consultas.');
  const [showSettings, setShowSettings] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const seededRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Seed inicial del chat (solo una vez)
  useEffect(() => {
    if (!auth.currentUser || !chatId || seededRef.current) return;
    const msgsRef = collection(db, ...getChatsPath(), chatId, 'mensajes');
    const q = query(msgsRef, limit(1));
    getDocs(q).then(snap => {
      if (snap.empty) {
        // 🚨 IMPORTANT: All resources MUST include organizationId field for Firestore security rules
        addDoc(msgsRef, {
          mensaje: '¡Hola! ¿En qué puedo ayudarte hoy?',
          remitente: 'asistente',
          origen: 'seed',
          timestamp: serverTimestamp(),
          organizationId: null, // ✅ Explicitly mark as personal resource
        }).finally(() => { seededRef.current = true; });
      } else {
        seededRef.current = true;
      }
    });
  }, [chatId]);

  // Listener de mensajes limpio
  useEffect(() => {
    if (!auth.currentUser || !chatId) return;

    const q = query(
      collection(db, ...getChatsPath(), chatId, 'mensajes'),
      orderBy('timestamp', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rows = [];
      snapshot.forEach(d => rows.push({ 
        id: d.id, 
        ...d.data(),
        timestamp: d.data().timestamp?.toDate()
      }));
      setMessages(rows);
    });

    return () => unsubscribe();
  }, [chatId]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || isSending) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsSending(true);
    setIsLoading(true);
    setIsTyping(true);

    try {
      // Create/update chat document with merge
      // 🚨 IMPORTANT: All resources MUST include organizationId field for Firestore security rules
      const chatRef = doc(db, ...getChatsPath(), chatId);
      await setDoc(chatRef, {
        chatId,
        ultimoMensaje: userMessage.substring(0, 200),
        lastMessageAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        organizationId: null, // ✅ Explicitly mark as personal resource (merge won't overwrite if exists)
      }, { merge: true });

      // Add user message to Firestore
      await addDoc(
        collection(db, ...getChatsPath(), chatId, 'mensajes'),
        {
          mensaje: userMessage,
          remitente: 'cliente',
          origen: 'usuario',
          timestamp: serverTimestamp(),
          organizationId: null, // ✅ Explicitly mark as personal resource
        }
      );

      // Call ChatGPT API through Firebase Function with idempotency
      const clientMessageId = uuidv4();
      const chatGPT = httpsCallable(functions, 'chatGPT');
      const result = await chatGPT({
        uid: auth.currentUser.uid,
        chatId: chatId,
        message: userMessage,
        origen: 'usuario',
        systemPrompt: systemPrompt,
        clientMessageId: clientMessageId
      });

      // Backend ya guarda la respuesta de IA, solo actualizamos metadatos
      const respuestaIA = result?.data?.respuesta || result?.data?.reply || 'Sin respuesta';
      await setDoc(chatRef, {
        ultimoMensaje: respuestaIA.substring(0, 200),
        lastMessageAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });

    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message
      // 🚨 IMPORTANT: All resources MUST include organizationId field for Firestore security rules
      await addDoc(
        collection(db, ...getChatsPath(), chatId, 'mensajes'),
        {
          mensaje: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta de nuevo.',
          remitente: 'sistema',
          timestamp: serverTimestamp(),
          organizationId: null, // ✅ Explicitly mark as personal resource
        }
      );
    } finally {
      setIsLoading(false);
      setIsTyping(false);
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text);
    // Show toast notification
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = 'Mensaje copiado';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  };

  const clearChat = async () => {
    if (!window.confirm('¿Estás seguro de que quieres limpiar el chat?')) return;
    
    // In production, you'd delete messages from Firestore
    // For now, just clear local state
    setMessages([]);
  };

  const regenerateLastResponse = async () => {
    const lastUserMessage = [...messages].reverse().find(m => m.remitente === 'cliente');
    if (lastUserMessage) {
      setInputMessage(lastUserMessage.mensaje);
      sendMessage();
    }
  };

  return (
    <div className="chat-gpt-interface">
      <div className="chat-header">
        <div className="chat-title">
          <FaRobot className="header-icon" />
          <h3>Chat con ChatGPT</h3>
        </div>
        <div className="chat-actions">
          <button onClick={() => setShowSettings(!showSettings)} className="settings-btn" title="Configuración">
            ⚙️
          </button>
          <button onClick={clearChat} className="clear-btn" title="Limpiar chat">
            <FaTrash />
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="settings-panel">
          <label>Prompt del Sistema:</label>
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            rows={3}
            placeholder="Define el comportamiento del asistente..."
          />
        </div>
      )}

      <div className="messages-container">
        {messages.length === 0 && (
          <div className="welcome-message">
            <FaRobot size={40} />
            <h4>¡Hola! Soy tu asistente con ChatGPT</h4>
            <p>¿En qué puedo ayudarte hoy?</p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`message ${message.remitente}`}
          >
            <div className="message-avatar">
              {message.remitente === 'cliente' ? <FaUser /> : <FaRobot />}
            </div>
            <div className="message-content">
              <div className="message-bubble">
                {message.mensaje}
              </div>
              <div className="message-meta">
                <span className="message-time">
                  {message.timestamp?.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
                <button 
                  onClick={() => copyMessage(message.mensaje)}
                  className="copy-btn"
                  title="Copiar mensaje"
                >
                  <FaCopy />
                </button>
              </div>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="message asistente typing">
            <div className="message-avatar">
              <FaRobot />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="input-container">
        <textarea
          ref={textareaRef}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Escribe tu mensaje..."
          disabled={isLoading}
          rows={1}
          className="message-input"
        />
        <button 
          onClick={regenerateLastResponse}
          disabled={isLoading || messages.length === 0}
          className="regenerate-btn"
          title="Regenerar última respuesta"
        >
          <FaRedo />
        </button>
        <button
          onClick={sendMessage}
          disabled={!inputMessage.trim() || isLoading || isSending}
          className="send-btn"
        >
          {isLoading ? <FaSpinner className="spinner" /> : <FaPaperPlane />}
        </button>
      </div>
    </div>
  );
};

export default ChatGPTInterface;